/**
 * Token MEDIALNY do <img src="/api/…?token=…"> (SEC 2026-07-17: TTL 15 min, przyjmują go tylko proxy zdjęć
 * i załączników; pełny token sesji nie trafia do URL-i ani access-logów nginx).
 *
 * 07.10.2026 (właściciel: „przy info o produkcie z GS1 nie ma zdjęcia, a na GS1 jest”): karta otwarta po
 * powrocie do zamrożonej przez Chrome zakładki rysowała obrazek ze starym tokenem (odświeżanie co 10 min nie
 * biegnie w zamrożonej karcie) → 401 → przeglądarka pokazywała zepsuty obrazek i nic go nie ponawiało.
 * Teraz: token odświeżany od razu po powrocie do karty, a obrazek proxy, który się nie załadował, jest ponawiany
 * RAZ ze świeżym tokenem. Gdy i to zawiedzie, obrazek dostaje zdarzenie `sb-media-blad` (moduł może pokazać
 * własny znak zastępczy, np. kafelek GS1).
 */

const W = window as any;
const ODSWIEZ_PO_MS = 9 * 60 * 1000;  // token żyje 15 min; po 9 min traktujemy go jak stary
let _wToku: Promise<string> | null = null;

function _sesja(): string {
  try { return localStorage.getItem('sellbase_token') || localStorage.getItem('orderflow_token') || ''; } catch (_e) { return ''; }
}

/** Pobiera nowy token medialny (jedno zapytanie naraz). Zwraca bieżący token (pusty, gdy brak sesji). */
export function odswiezTokenMedialny(): Promise<string> {
  if (_wToku) return _wToku;
  const sesja = _sesja();
  if (!sesja) return Promise.resolve(String(W.__SB_MEDIA_TOKEN__ || ''));
  // Limit czasu: wiszące zapytanie blokowałoby wszystkie kolejne odświeżenia (jedno zapytanie naraz).
  const AS: any = typeof AbortSignal !== 'undefined' ? AbortSignal : null;
  const sygnal = AS && typeof AS.timeout === 'function' ? AS.timeout(10000) : undefined;
  _wToku = fetch('/api/auth/media-token', { headers: { 'Authorization': 'Bearer ' + sesja }, signal: sygnal })
    .then(r => (r.ok ? r.json() : null))
    .then((d: any) => {
      if (d && d.token) { W.__SB_MEDIA_TOKEN__ = d.token; W.__SB_MEDIA_TOKEN_AT__ = Date.now(); }
      return String(W.__SB_MEDIA_TOKEN__ || '');
    })
    .catch(() => String(W.__SB_MEDIA_TOKEN__ || ''))
    .finally(() => { _wToku = null; });
  return _wToku;
}

export function tokenMedialnyStary(teraz = Date.now()): boolean {
  const at = Number(W.__SB_MEDIA_TOKEN_AT__ || 0);
  return !W.__SB_MEDIA_TOKEN__ || !at || teraz - at > ODSWIEZ_PO_MS;
}

/** FIL-001 (audyt Codex 06.10.2026): prywatne pliki w `/uploads` (zdjęcia spraw, zrzuty zgłoszeń, zdjęcia notatek
 *  produktu, załączniki szablonów e-mail) serwer wydaje tylko z tokenem i dla firmy pliku (core/pliki_prywatne.py).
 *  Zdjęcia produktów (`/uploads/product_images/`) zostają publiczne. */
export const PRYWATNE_UPLOADS = /^\/uploads\/(internal_tasks|bug_reports|product_comments|email_attachments)\//;

/** Adres prywatnego pliku z bieżącym tokenem medialnym w `?t=` (pusty token też — po 401 obrazek ponowi
 *  się ze świeżym). Inne adresy wracają bez zmian. */
export function adresPlikuPrywatnego(src: any, origin: string = window.location.origin): string {
  const surowy = String(src == null ? '' : src).trim();
  if (!surowy) return '';
  try {
    const u = new URL(surowy, origin);
    if (u.origin !== origin || !PRYWATNE_UPLOADS.test(u.pathname)) return surowy;
    u.searchParams.set('t', String(W.__SB_MEDIA_TOKEN__ || ''));
    return u.pathname + u.search;
  } catch (_e) { return surowy; }
}

/** Adres proxy z tokenem medialnym w `?token=` albo `?t=` (załączniki reklamacji) — tylko ten sam host i /api/
 *  (od FIL-001 także prywatne `/uploads`). */
export function adresProxyZTokenem(src: string, origin: string): { u: URL; parametr: string } | null {
  try {
    const u = new URL(src, origin);
    if (u.origin !== origin || !(u.pathname.startsWith('/api/') || PRYWATNE_UPLOADS.test(u.pathname))) return null;
    const parametr = u.searchParams.has('token') ? 'token' : (u.searchParams.has('t') ? 't' : '');
    return parametr ? { u, parametr } : null;
  } catch (_e) { return null; }
}

function _porazka(img: HTMLImageElement): void {
  img.setAttribute('data-sb-media-ponow', '2');
  img.dispatchEvent(new CustomEvent('sb-media-blad', { bubbles: true }));
}

/** Ponowienie ma sens tylko z INNYM tokenem: świeży bieżący (np. pusty w URL przy starcie) albo nowo pobrany,
 *  gdy bieżący jest stary. Ten sam świeży token = to nie było 401 (np. 404/502) → od razu porażka, bez obciążania
 *  serwera drugim pobraniem (lista GS1: semafor pobrań z MojeGS1). */
async function _ponowObrazek(img: HTMLImageElement): Promise<void> {
  const proba = img.getAttribute('data-sb-media-ponow');
  if (proba === '1') { _porazka(img); return; }  // ponowienie też się nie udało
  if (proba) return;
  const a = adresProxyZTokenem(img.currentSrc || img.src, window.location.origin);
  if (!a) return;
  img.setAttribute('data-sb-media-ponow', '1');
  // Stan sprzed `onerror` elementu (biegnie PO tym nasłuchu): wiadomości/reklamacje chowają obrazek
  // i pokazują sąsiada-zastępstwo — po udanym ponowieniu wracamy do stanu sprzed błędu.
  const styl = img.style ? img.style.display : '';
  const sasiad = img.nextElementSibling as HTMLElement | null;
  const stylSasiada = sasiad && sasiad.style ? sasiad.style.display : null;
  const wUrl = a.u.searchParams.get(a.parametr) || '';
  let tk = String(W.__SB_MEDIA_TOKEN__ || '');
  if (!tk || tk === wUrl || tokenMedialnyStary()) {
    if (tk && tk === wUrl && !tokenMedialnyStary()) { _porazka(img); return; }
    tk = await odswiezTokenMedialny();
  }
  if (!tk || tk === wUrl || !img.isConnected) { _porazka(img); return; }  // odświeżenie nie dało nowego tokena
  img.addEventListener('load', () => {
    img.removeAttribute('data-sb-media-ponow');
    if (img.style) img.style.display = styl;
    if (sasiad && sasiad.style && stylSasiada !== null) sasiad.style.display = stylSasiada;
  }, { once: true });
  a.u.searchParams.set(a.parametr, tk);
  img.src = a.u.pathname + a.u.search;
}

let _zainstalowany = false;
/** Czy obrazki proxy są ponawiane (moduły z własnym znakiem zastępczym czekają wtedy na `sb-media-blad`). */
export const ponawianieAktywne = (): boolean => _zainstalowany;
export function zainstalujTokenMedialny(): void {
  if (_zainstalowany) return;
  _zainstalowany = true;
  void odswiezTokenMedialny();
  try { setInterval(() => { void odswiezTokenMedialny(); }, 10 * 60 * 1000); } catch (_e) { /* noop */ }
  // Audyt bezpieczeństwa 01.10.2026 (2.5): call-site'y nie podstawiają już pełnego tokena sesji do URL —
  // po zalogowaniu (brak sesji przy starcie) dociągamy token medialny w ciągu kilku sekund.
  try { setInterval(() => { if (!W.__SB_MEDIA_TOKEN__) void odswiezTokenMedialny(); }, 5000); } catch (_e) { /* noop */ }
  // Powrót do karty (także po zamrożeniu przez Chrome): stary token wymieniamy od razu, nie po 10 min.
  const poPowrocie = () => { if (document.visibilityState !== 'hidden' && tokenMedialnyStary()) void odswiezTokenMedialny(); };
  try {
    document.addEventListener('visibilitychange', poPowrocie);
    document.addEventListener('resume', poPowrocie);
    window.addEventListener('pageshow', poPowrocie);
    window.addEventListener('focus', poPowrocie);
  } catch (_e) { /* noop */ }
  // FIL-001: link do prywatnego pliku (miniatura → pełny obraz w nowej karcie) dostaje token w chwili kliknięcia —
  // ten z renderu mógł wygasnąć (15 min). Stary token: najpierw odświeżenie, potem otwarcie.
  const linkPrywatny = (e: Event): { a: HTMLAnchorElement; u: URL } | null => {
    const t = e.target as HTMLElement | null;
    const a = t && t.closest ? (t.closest('a[href]') as HTMLAnchorElement | null) : null;
    if (!a) return null;
    let u: URL;
    try { u = new URL(a.getAttribute('href') || '', window.location.origin); } catch (_e) { return null; }
    if (u.origin !== window.location.origin || !PRYWATNE_UPLOADS.test(u.pathname)) return null;
    return { a, u };
  };
  try {
    // Środkowy przycisk, przeciągnięcie, „otwórz w nowej karcie” z menu: bieżący token wstawiamy już przy
    // wciśnięciu przycisku (odświeżany co 10 min, ważny 15 min — prawie zawsze świeży).
    document.addEventListener('mousedown', (e: Event) => {
      const l = linkPrywatny(e);
      if (l) l.a.setAttribute('href', adresPlikuPrywatnego(l.u.pathname));
    }, true);
    document.addEventListener('click', (e: Event) => {
      const l = linkPrywatny(e);
      if (!l) return;
      if (!tokenMedialnyStary()) { l.a.setAttribute('href', adresPlikuPrywatnego(l.u.pathname)); return; }
      e.preventDefault();
      const nowaKarta = (l.a.getAttribute('target') || '_self') === '_blank';
      const okno = nowaKarta ? window.open('about:blank', '_blank') : null;
      void odswiezTokenMedialny().then(() => {
        const adres = adresPlikuPrywatnego(l.u.pathname);
        if (okno) { try { okno.opener = null; okno.location.href = adres; } catch (_e) { window.open(adres, '_blank', 'noopener'); } }
        else if (nowaKarta) window.open(adres, '_blank', 'noopener');  // blokada okien: nie wyrzucamy użytkownika z aplikacji
        else window.location.href = adres;
      });
    }, true);
  } catch (_e) { /* noop */ }
  // Błąd ładowania obrazka nie „bąbelkuje” — łapiemy go w fazie przechwytywania.
  try {
    document.addEventListener('error', (e: Event) => {
      const el = e.target as HTMLElement | null;
      if (el && el.tagName === 'IMG') void _ponowObrazek(el as HTMLImageElement);
    }, true);
  } catch (_e) { /* noop */ }
}
